import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { ProgressSpinner } from "primereact/progressspinner";
import { ROUTES } from "../../config/routes";
import {
  DSRDashboardMetric,
  dsrAuditsApi,
} from "../../api/dsrAuditsApi";

interface DSRToolCard {
  title: string;
  description: string;
  route: string;
  status: "healthy" | "warning" | "critical" | "info";
  metrics?: {
    totalReports: number;
    availableReports: number;
    availabilityRate: number;
    queueTotal: number;
    cleanRecords: number;
    pendingReviewCount: number;
    issueRecords: number;
    reportRowCount: number;
    totalRowsEvaluated: number;
    /** Report rows / total rows evaluated (report + queue), 0–100. */
    inclusionRatePercent: number;
    latestReportLabel: string;
    hasQueueData: boolean;
    dataWarning?: string;
  };
}

type ReportType = "genent" | "music-videos" | "sound-recordings";

const DSR_TOOL_CONFIG: Array<
  Omit<DSRToolCard, "status" | "metrics"> & { reportType: ReportType }
> = [
  {
    title: "GenEnt Reports",
    description: "Generate and view GenEnt-specific DSR audit reports.",
    route: ROUTES.DSR_GENENT,
    reportType: "genent",
  },
  {
    title: "Music Videos Reports",
    description: "DSR audit reports specifically for music video assets.",
    route: ROUTES.DSR_MUSIC_VIDEOS,
    reportType: "music-videos",
  },
  {
    title: "Sound Recordings Reports",
    description: "DSR audit reports for sound recording assets.",
    route: ROUTES.DSR_SOUND_RECORDINGS,
    reportType: "sound-recordings",
  },
];

const STALE_REPORT_THRESHOLD_DAYS = 31;
const floorInclusionPercent = (value: number) => Math.floor(value);

const DSRAuditsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dsrTools, setDsrTools] = useState<DSRToolCard[]>([]);
  const [globalError, setGlobalError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadDsrDashboardMetrics = async () => {
      setLoading(true);
      setGlobalError(null);
      const response = await dsrAuditsApi.getDashboardMetrics();

      const metricByType: Record<ReportType, DSRDashboardMetric> = {
        genent: response.genent,
        "music-videos": response["music-videos"],
        "sound-recordings": response["sound-recordings"],
      };

      const nextCards = DSR_TOOL_CONFIG.map((tool) => {
        const metric = metricByType[tool.reportType];
        const dataWarningParts: string[] = [];
        if (metric.stale) {
          dataWarningParts.push(
            `Latest available report is older than ${STALE_REPORT_THRESHOLD_DAYS} days or missing`
          );
        }

        return {
          title: tool.title,
          description: tool.description,
          route: tool.route,
          status: metric.status,
          metrics: {
            totalReports: metric.totalReports,
            availableReports: metric.availableReports,
            availabilityRate: metric.availabilityRate,
            queueTotal: metric.latestQueueTotal,
            cleanRecords: metric.latestQueueCleanRecords,
            pendingReviewCount: metric.pendingReviewCount,
            issueRecords: metric.latestQueueIssueRecords,
            reportRowCount: metric.reportRowCount,
            totalRowsEvaluated: metric.totalRowsEvaluated,
            inclusionRatePercent: metric.inclusionRatePercent,
            latestReportLabel:
              metric.latestReportAgeDays === null
                ? "No reports yet"
                : `${metric.latestReportAgeDays}d ago`,
            hasQueueData: metric.latestQueueTotal > 0,
            dataWarning:
              dataWarningParts.length > 0 ? dataWarningParts.join(" • ") : undefined,
          },
        } as DSRToolCard;
      });

      if (isMounted) {
        setDsrTools(nextCards);
        setLoading(false);
      }
    };

    loadDsrDashboardMetrics().catch((error) => {
      console.error("Error loading DSR dashboard metrics:", error);
      if (isMounted) {
        setGlobalError(
          "Unable to load DSR metrics right now. You can still access each report tool."
        );
        setDsrTools(
          DSR_TOOL_CONFIG.map((tool) => ({
            ...tool,
            status: "info",
            metrics: {
              totalReports: 0,
              availableReports: 0,
              availabilityRate: 0,
              queueTotal: 0,
              cleanRecords: 0,
              pendingReviewCount: 0,
              issueRecords: 0,
              reportRowCount: 0,
              totalRowsEvaluated: 0,
              inclusionRatePercent: 0,
              latestReportLabel: "No reports yet",
              hasQueueData: false,
              dataWarning: "Metric data unavailable",
            },
          }))
        );
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const dashboardSummary = useMemo(() => {
    const allMetrics = dsrTools.map((tool) => tool.metrics).filter(Boolean);
    const totalRowsEvaluated = allMetrics.reduce(
      (sum, metric) => sum + (metric?.totalRowsEvaluated || 0),
      0
    );
    const totalIncluded = allMetrics.reduce(
      (sum, metric) => sum + (metric?.reportRowCount || 0),
      0
    );

    return {
      inclusionRatePercent:
        totalRowsEvaluated > 0
          ? floorInclusionPercent(
              (totalIncluded / totalRowsEvaluated) * 100
            )
          : 0,
      totalRowsEvaluated,
      totalIncluded,
    };
  }, [dsrTools]);

  /** Inclusion rate: green >95%, yellow 90–95%, red <90%. */
  const getInclusionBarColor = (inclusionPercent: number) => {
    if (inclusionPercent > 95) return "#22c55e";
    if (inclusionPercent >= 90) return "#eab308";
    return "#ef4444";
  };

  return (
    <div className="space-y-6 w-full min-w-0 max-w-full">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold app-menubar-title mb-2">
          DSR Audits Dashboard
        </h1>
        <p className="p-subtitle">
          Digital Service Rights audit reports for comprehensive rights
          management
        </p>
      </div>

      {globalError && (
        <div className="p-tag-warning rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {globalError}
        </div>
      )}

      {loading && (
        <Card>
          <div className="flex justify-center items-center h-44">
            <ProgressSpinner />
          </div>
        </Card>
      )}

      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch min-w-0">
          {dsrTools.map((tool, index) => (
            <Card
              key={index}
              className="h-full min-w-0 max-w-full hover:shadow-lg transition-shadow cursor-pointer [&_.p-card-body]:min-w-0 [&_.p-card-content]:min-w-0"
              onClick={() => navigate(tool.route)}
            >
              <div className="min-w-0 break-words">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold app-menubar-title break-words">
                    {tool.title}
                  </h3>
                </div>

                <div className="space-y-3 min-w-0">
                  <p className="text-sm p-subtitle break-words">
                    {tool.description}
                  </p>

                  {tool.metrics && (
                    <div className="space-y-2 min-w-0 break-words">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1 text-sm min-w-0">
                        <span className="p-subtitle min-w-0 break-words">
                          Row inclusion rate
                        </span>
                        <span
                          className="font-medium shrink-0"
                          style={{
                            color: getInclusionBarColor(
                              tool.metrics.inclusionRatePercent
                            ),
                          }}
                        >
                          {tool.metrics.inclusionRatePercent}%
                        </span>
                      </div>
                    <ProgressBar
                      value={tool.metrics.inclusionRatePercent || 0}
                      color={getInclusionBarColor(
                        tool.metrics.inclusionRatePercent
                      )}
                      showValue={false}
                      className="h-2 min-w-0 max-w-full"
                    />
                    <div className="text-xs text-gray-500 break-words">
                      Rows in report: {tool.metrics.reportRowCount} of{" "}
                      {tool.metrics.totalRowsEvaluated} evaluated
                      {tool.metrics.totalRowsEvaluated > 0
                        ? ` (${tool.metrics.inclusionRatePercent}% included)`
                        : ""}
                    </div>
                    <div className="text-xs text-gray-500 break-words">
                      Report availability: {tool.metrics.availableReports}/
                      {tool.metrics.totalReports} ({tool.metrics.availabilityRate}%)
                    </div>
                    <div className="text-xs text-gray-500 break-words">
                      Latest report: {tool.metrics.latestReportLabel}
                    </div>
                    {!tool.metrics.hasQueueData &&
                      tool.metrics.totalRowsEvaluated > 0 && (
                        <div className="text-xs text-blue-600 break-words">
                          No rows in the audit queue for this run—
                          {tool.metrics.inclusionRatePercent}% inclusion (all{" "}
                          {tool.metrics.reportRowCount} evaluated row
                          {tool.metrics.reportRowCount === 1 ? "" : "s"} in the
                          report).
                        </div>
                      )}
                    {tool.metrics.dataWarning && (
                      <div className="text-xs text-orange-600 break-words break-all">
                        {tool.metrics.dataWarning}
                      </div>
                    )}
                  </div>
                )}

                <Button
                  label="Access Tool"
                  className="w-full mt-4 max-w-full"
                  severity="secondary"
                  outlined
                />
              </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {!loading && (
        <div className="p-summary-card rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 min-w-0 break-words">
          Overall row inclusion rate across DSR streams:{" "}
          <span className="font-semibold">
            {dashboardSummary.inclusionRatePercent}%
          </span>
          {dashboardSummary.totalRowsEvaluated > 0 ? (
            <>
              {" "}
              (
              <span className="font-semibold">
                {dashboardSummary.totalIncluded}
              </span>{" "}
              included in reports of{" "}
              <span className="font-semibold">
                {dashboardSummary.totalRowsEvaluated}
              </span>{" "}
              rows evaluated in latest runs).
            </>
          ) : (
            <> (no row totals yet—generate a report per stream).</>
          )}
        </div>
      )}

      <div className="mt-8 p-6 p-text-info rounded-lg">
        <h3 className="text-lg font-semibold app-menubar-title mb-2">
          About DSR Audits
        </h3>
        <p className="p-text-info-content">
          Digital Service Rights (DSR) audits ensure compliance with digital
          rights management requirements and help identify any discrepancies in
          rights ownership or licensing. These reports are essential for
          maintaining proper rights management across all digital assets.
        </p>
      </div>
    </div>
  );
};

export default DSRAuditsDashboard;
